<template>
	<div class="modal fade" :id="modalId" tabindex="-1" aria-hidden="true">
		<div class="modal-dialog modal-lg modal-dialog-scrollable">
			<div class="modal-content">
				<form @submit.prevent="submit()">
					<div class="modal-header">
						<div>
							<h5 class="modal-title">{{ title }}</h5>
							<div class="text-muted small" v-if="mode === 'removal'">The account is kept and hidden, not erased. This can be undone.</div>
						</div>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>

					<div class="modal-body">
						<div class="alert alert-danger py-2" v-if="error">{{ error }}</div>

						<!-- WHY is it going away. Only "cancelled" needs a reason: most removals are
							duplicate merges, and asking everyone would fill the column with noise. -->
						<div class="form-group mb-3" v-if="mode === 'removal'">
							<label class="fw-bold">Why is this account being removed?</label>
							<div class="d-flex flex-column gap-2">
								<label v-for="k in removalKinds" :key="k.value" class="kind" :class="{ picked: kind === k.value }">
									<input type="radio" class="form-check-input me-2" :value="k.value" v-model="kind" />
									<span>
										<strong>{{ k.label }}</strong>
										<span class="d-block text-muted small">{{ k.help }}</span>
									</span>
								</label>
							</div>
						</div>

						<div v-if="kind === 'duplicate'">
							<div class="form-group mb-2">
								<label>Keep this account instead <span class="text-danger">*</span></label>
								<input type="number" class="form-control" v-model="mergeInto" placeholder="Client ID to merge into" />
								<div class="form-text">Live subscriptions move to that account. Nothing is cancelled, so no reason is needed.</div>
							</div>
						</div>

						<div v-if="kind === 'cancelled'">
							<div class="form-group mb-3" v-if="churnedSubscriptions.length">
								<label>What are they leaving?</label>
								<div class="d-flex flex-column gap-1">
									<label v-for="s in churnedSubscriptions" :key="s.id" class="d-flex align-items-center gap-2 border rounded px-3 py-2 mb-0">
										<input type="checkbox" class="form-check-input mt-0" :value="s.id" v-model="subscriptionIds" />
										<span class="fw-bold flex-grow-1">{{ brandLabel(s) }}</span>
										<span class="badge bg-light text-muted">{{ s.status }}</span>
									</label>
								</div>
							</div>

							<div class="form-group mb-3">
								<label>
									What led them to cancel? <span class="text-danger">*</span>
									<span class="text-muted fw-normal">select all that apply</span>
								</label>
								<div class="row g-2 border rounded px-2 py-2 mx-0">
									<div class="col-6" v-for="r in availableReasons" :key="r.code">
										<label class="d-flex align-items-center gap-2 mb-0">
											<input type="checkbox" class="form-check-input mt-0" :value="r.code" v-model="reasons" />
											<span>{{ r.label }}</span>
										</label>
									</div>
								</div>
							</div>

							<div class="row g-3 mb-3">
								<div class="col-6">
									<label>Who are they going to</label>
									<input type="text" class="form-control" v-model="competitor" placeholder="Competitor name" />
								</div>
								<div class="col-6">
									<label>What they will pay there</label>
									<div class="input-group">
										<span class="input-group-text">$</span>
										<input type="number" step="0.01" class="form-control" v-model="competitorAnnualCost" placeholder="Annual" />
									</div>
								</div>
							</div>

							<div class="row g-3 mb-3">
								<div class="col-6">
									<label>Effective date</label>
									<input type="date" class="form-control" v-model="effectiveDate" />
								</div>
								<div class="col-6">
									<label>Would they refer us?</label>
									<div class="btn-group w-100">
										<button type="button" class="btn" :class="wouldRefer === true ? 'btn-success' : 'btn-outline-secondary'" @click="wouldRefer = wouldRefer === true ? null : true">Yes</button>
										<button type="button" class="btn" :class="wouldRefer === false ? 'btn-danger' : 'btn-outline-secondary'" @click="wouldRefer = wouldRefer === false ? null : false">No</button>
									</div>
								</div>
							</div>

							<div class="form-group">
								<label>What they actually said</label>
								<textarea class="form-control" rows="3" v-model="note"></textarea>
								<div class="form-text">Their words from the exit form or the pre-exit call.</div>
							</div>
						</div>
					</div>

					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
						<button type="submit" class="btn" :class="mode === 'removal' && kind === 'cancelled' ? 'btn-danger' : 'btn-primary'" :disabled="saving">
							{{ saving ? 'Saving...' : submitLabel }}
						</button>
					</div>
				</form>
			</div>
		</div>
	</div>
</template>

<script>
import restApi from '../../api/index.js'
import { handleErrors } from '../../actions/helpers'

/**
 * One modal, two jobs.
 *
 *  mode="removal"  the client card's remove button: asks why the account is going away and only
 *                  demands a reason when the answer is "they cancelled"
 *  mode="reason"   the cancellation-reason worklist: records the reason and leaves the account
 *                  exactly where it is, which is how the churned accounts get covered at all,
 *                  since a churned account is never removed
 */
export default {
	name: 'CancellationModal',
	props: {
		modalId: { type: String, default: 'cancellationModal' },
		mode: { type: String, default: 'removal' },
		clientId: { type: [Number, String], default: null },
		clientName: { type: String, default: 'this account' },
		subscriptions: { type: Array, default: () => [] },
		/** the subscription being edited: its stored answer fills the form so this is an edit */
		editing: { type: Object, default: null },
	},
	data() {
		return {
			kind: this.mode === 'reason' ? 'cancelled' : null,
			reasons: [],
			subscriptionIds: [],
			competitor: '',
			competitorAnnualCost: '',
			wouldRefer: null,
			effectiveDate: new Date().toISOString().slice(0, 10),
			note: '',
			mergeInto: '',
			allReasons: [],
			saving: false,
			error: '',
			removalKinds: [
				{ value: 'cancelled', label: 'They cancelled', help: 'A real customer left. A reason is required.' },
				{ value: 'duplicate', label: 'Duplicate record', help: 'Same town already exists. Pick the account to keep.' },
				{ value: 'not_a_customer', label: 'Never a customer', help: 'Test, junk or a record created by mistake.' },
			],
		}
	},
	computed: {
		churnedSubscriptions() {
			if (this.editing) return [this.editing]
			const churned = ['lost', 'cancelled_after_trial', 'canceled']
			return (this.subscriptions || []).filter(s => churned.includes(s.status))
		},
		// the brands this account actually has, so Town Web wording never shows on a
		// ClerkMinutes-only account. The API eager-loads subscriptions.brand as an OBJECT, so the
		// key has to be read out of it; treating it as a string printed raw JSON in the list and
		// left the reason checkboxes empty, because nothing ever matched.
		brandsOnAccount() {
			return (this.subscriptions || []).map(s => this.brandKey(s)).filter(Boolean)
		},
		availableReasons() {
			const brands = this.brandsOnAccount.length ? this.brandsOnAccount : ['town_web']
			return this.allReasons.filter(r => r.brands.some(b => brands.includes(b)))
		},
		title() {
			if (this.mode === 'removal') return 'Remove ' + this.clientName
			if (this.editingHasReason) return 'Edit why ' + this.clientName + ' left'
			return 'Why did ' + this.clientName + ' leave?'
		},
		editingHasReason() {
			return !!(this.editing && (this.editing.cancel_recorded_at || this.codesOf(this.editing).length))
		},
		submitLabel() {
			if (this.mode === 'reason') return this.editingHasReason ? 'Update reason' : 'Save reason'
			if (this.kind === 'cancelled') return 'Save reason and remove'
			if (this.kind === 'duplicate') return 'Merge and remove'
			return 'Remove'
		},
	},
	watch: {
		// reopening for a different subscription must not show the last one's answer
		editing: {
			immediate: true,
			handler(sub) {
				this.applyEditing(sub)
			},
		},
	},
	mounted() {
		this.loadReasons()
	},
	methods: {
		/** fill the form from a stored answer, or clear it for a fresh one */
		applyEditing(sub) {
			this.error = ''
			if (!sub) {
				this.reasons = []
				this.subscriptionIds = []
				this.competitor = ''
				this.competitorAnnualCost = ''
				this.wouldRefer = null
				this.note = ''
				this.effectiveDate = new Date().toISOString().slice(0, 10)
				return
			}
			this.reasons = this.codesOf(sub)
			this.subscriptionIds = [sub.id]
			this.competitor = sub.lost_to_competitor || ''
			this.competitorAnnualCost = sub.competitor_annual_cost == null ? '' : sub.competitor_annual_cost
			this.wouldRefer = sub.would_refer === null || sub.would_refer === undefined ? null : !!sub.would_refer
			this.note = sub.cancel_reason || ''
			this.effectiveDate = (sub.canceled_at || '').toString().slice(0, 10) || new Date().toISOString().slice(0, 10)
		},
		codesOf(sub) {
			const raw = sub && sub.cancel_reasons
			if (!raw) return []
			if (Array.isArray(raw)) return raw
			try {
				const parsed = JSON.parse(raw)
				return Array.isArray(parsed) ? parsed : []
			} catch (e) {
				return []
			}
		},
		loadReasons() {
			restApi
				.get('/cancellations/options')
				.then(res => {
					this.allReasons = res.data.reasons || []
				})
				.catch(() => {
					this.error = 'Could not load the reason list.'
				})
		},
		/** brand arrives as the eager-loaded relation, or occasionally as a bare key */
		brandKey(sub) {
			if (!sub) return null
			const b = sub.brand
			if (!b) return sub.brand_key || null
			return typeof b === 'string' ? b : b.key || null
		},
		brandLabel(sub) {
			const key = typeof sub === 'string' ? sub : this.brandKey(sub)
			return { town_web: 'Town Web', heygov: 'HeyGov', clerkminutes: 'ClerkMinutes' }[key] || key || 'Subscription'
		},
		payload() {
			return {
				reasons: this.reasons,
				note: this.note || null,
				competitor: this.competitor || null,
				competitor_annual_cost: this.competitorAnnualCost === '' ? null : this.competitorAnnualCost,
				would_refer: this.wouldRefer,
				effective_date: this.effectiveDate,
				subscription_ids: this.subscriptionIds.length ? this.subscriptionIds : null,
			}
		},
		submit() {
			this.error = ''
			if (this.mode === 'removal' && !this.kind) {
				this.error = 'Say why this account is being removed.'
				return
			}
			if (this.kind === 'cancelled' && !this.reasons.length) {
				this.error = 'Pick at least one reason.'
				return
			}
			if (this.kind === 'duplicate' && !this.mergeInto) {
				this.error = 'Pick the account to keep.'
				return
			}
			this.saving = true

			const done = message => {
				this.saving = false
				this.$emit('saved', message)
			}
			const failed = e => {
				this.saving = false
				this.error = handleErrors(e, 'Could not save that.')
			}

			if (this.mode === 'reason') {
				restApi
					.post(`/clients/${this.clientId}/cancellation`, { ...this.payload(), source: 'worklist' })
					.then(() => done('Reason recorded'))
					.catch(failed)
				return
			}

			restApi
				.delete(`/clients/${this.clientId}`, {
					data: {
						removal_kind: this.kind,
						merge_into: this.kind === 'duplicate' ? this.mergeInto : null,
						source: 'removal',
						...this.payload(),
					},
				})
				.then(() => done('Client removed'))
				.catch(failed)
		},
	},
}
</script>

<style scoped>
.kind {
	display: flex;
	align-items: flex-start;
	gap: 6px;
	border: 1px solid #dfe4ea;
	border-radius: 8px;
	padding: 11px 13px;
	margin-bottom: 0;
	cursor: pointer;
}
.kind.picked {
	border: 2px solid #2196f3;
	background: #f4f9ff;
}
</style>
